import { useState } from 'react';
import { HoldReasonSelect } from './HoldReasonSelect';
import { useHoldProductionOrder } from '../hooks/useHoldDecisionActions';

interface HoldProductionOrderDialogProps {
  productionOrderId: string;
  open: boolean;
  onClose: () => void;
  /** Optional: pre-link an open DowntimeRecord that caused this hold (Rule HLD-07). */
  downtimeRecordId?: string;
}

/**
 * AM-015 Rule HLD-01: the UI-level enforcement of "reason is mandatory".
 * Submit is disabled until a reason is selected — mirrors the backend's
 * hard requirement rather than relying on the 400 response alone.
 */
export function HoldProductionOrderDialog({
  productionOrderId,
  open,
  onClose,
  downtimeRecordId,
}: HoldProductionOrderDialogProps) {
  const [reasonCodeId, setReasonCodeId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const { mutate, isPending, error } = useHoldProductionOrder(productionOrderId);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reasonCodeId) return;
    mutate(
      { reason_code_id: reasonCodeId, notes, downtime_record_id: downtimeRecordId ?? null },
      { onSuccess: onClose }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
      <form onSubmit={handleSubmit} className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">توقف کامل خط</h2>
        <p className="mb-4 text-sm text-gray-500">
          این سفارش و تمام Work Order های در حال اجرای آن متوقف می‌شوند. لطفاً دلیل را انتخاب کنید.
        </p>

        <div className="mb-4">
          <HoldReasonSelect value={reasonCodeId} onChange={setReasonCodeId} />
        </div>

        <div className="mb-4 flex flex-col gap-1">
          <label htmlFor="hold-notes" className="text-sm font-medium text-gray-700">
            توضیحات (اختیاری)
          </label>
          <textarea
            id="hold-notes"
            className="min-h-[80px] rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="مثلاً: منتظر رسیدن مواد اولیه از انبار مرکزی"
          />
        </div>

        {error && (
          <p className="mb-3 text-sm text-red-600">
            خطا در ثبت توقف. لطفاً دوباره تلاش کنید.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
            disabled={isPending}
          >
            انصراف
          </button>
          <button
            type="submit"
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            disabled={!reasonCodeId || isPending}
          >
            {isPending ? 'در حال ثبت…' : 'تأیید توقف خط'}
          </button>
        </div>
      </form>
    </div>
  );
}
